import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Button,
  FormSection,
  Select,
  Switch,
  RadioGroup,
  Input,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { resolveTheme, resolveThemeFromConfig } from '@/shared/theme/themeResolver';
import { getThemeCSSVars } from '@/shared/theme/cssVariableMapper';
import type { TenantThemeOverrides, IconPackId, RadiusScale, AppResponse } from '@troia-v3/types';

// ── Helpers ──────────────────────────────────────────────────────────────────

type PageBackgroundType = 'solid' | 'gradient' | 'image' | 'image-overlay';

interface DraftState {
  // Colors
  primary: string;
  primaryHover: string;
  success: string;
  warning: string;
  error: string;
  // Typography
  fontFamily: string;
  // Background
  bgType: PageBackgroundType;
  bgValue: string;
  bgOverlay: string;
  // Icons
  iconPack: IconPackId;
  strokeWidth: number;
  // Radius
  radiusScale: RadiusScale;
  // Borders
  borderWidth: '0.5px' | '1px';
  // Dark Mode
  darkEnabled: boolean;
  darkPrimary: string;
  darkPrimaryHover: string;
}

const DEFAULT_DRAFT: DraftState = {
  primary: '#8b5cf6',
  primaryHover: '#7c3aed',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  fontFamily: 'Inter',
  bgType: 'solid',
  bgValue: '#f9fafb',
  bgOverlay: '',
  iconPack: 'hi2',
  strokeWidth: 1.5,
  radiusScale: 'moderate',
  borderWidth: '1px',
  darkEnabled: true,
  darkPrimary: '',
  darkPrimaryHover: '',
};

const FONT_OPTIONS = [
  { value: 'Inter', label: 'Inter' },
  { value: 'Geist', label: 'Geist' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Nunito', label: 'Nunito' },
  { value: '"DM Sans"', label: 'DM Sans' },
  { value: '"Plus Jakarta Sans"', label: 'Plus Jakarta Sans' },
];

const ICON_PACK_OPTIONS = [
  { value: 'hi2', label: 'Heroicons 2 (hi2)' },
  { value: 'lu', label: 'Lucide (lu)' },
  { value: 'fi', label: 'Feather Icons (fi)' },
  { value: 'tb', label: 'Tabler Icons (tb)' },
];

const RADIUS_OPTIONS = [
  { value: 'conservative', label: 'Conservador', description: 'Cantos mais retos (4–8px)' },
  { value: 'moderate', label: 'Moderado', description: 'Equilíbrio (6–12px)' },
  { value: 'rounded', label: 'Arredondado', description: 'Cantos mais suaves (8–16px)' },
];

const BORDER_WIDTH_OPTIONS = [
  { value: '0.5px', label: '0.5px' },
  { value: '1px', label: '1px' },
];

const BG_TYPE_OPTIONS = [
  { value: 'solid', label: 'Cor sólida' },
  { value: 'gradient', label: 'Gradiente' },
  { value: 'image', label: 'Imagem' },
  { value: 'image-overlay', label: 'Imagem com overlay' },
];

const STROKE_OPTIONS = [
  { value: '1', label: '1' },
  { value: '1.5', label: '1.5' },
  { value: '2', label: '2' },
  { value: '2.5', label: '2.5' },
  { value: '3', label: '3' },
];

function overridesToDraft(overrides: TenantThemeOverrides | undefined): DraftState {
  if (!overrides) return { ...DEFAULT_DRAFT };

  return {
    primary: overrides.colors?.primary ?? DEFAULT_DRAFT.primary,
    primaryHover: overrides.colors?.primaryHover ?? DEFAULT_DRAFT.primaryHover,
    success: overrides.colors?.success ?? DEFAULT_DRAFT.success,
    warning: overrides.colors?.warning ?? DEFAULT_DRAFT.warning,
    error: overrides.colors?.error ?? DEFAULT_DRAFT.error,
    fontFamily: overrides.typography?.fontFamily ?? DEFAULT_DRAFT.fontFamily,
    bgType: overrides.background?.page?.type ?? DEFAULT_DRAFT.bgType,
    bgValue: overrides.background?.page?.value ?? DEFAULT_DRAFT.bgValue,
    bgOverlay: overrides.background?.page?.overlay ?? DEFAULT_DRAFT.bgOverlay,
    iconPack: overrides.icons?.pack ?? DEFAULT_DRAFT.iconPack,
    strokeWidth: overrides.icons?.strokeWidth ?? DEFAULT_DRAFT.strokeWidth,
    radiusScale: overrides.radius?.scale ?? DEFAULT_DRAFT.radiusScale,
    borderWidth: overrides.borders?.width ?? DEFAULT_DRAFT.borderWidth,
    darkEnabled: overrides.darkMode?.enabled ?? DEFAULT_DRAFT.darkEnabled,
    darkPrimary: overrides.darkMode?.colors?.primary ?? DEFAULT_DRAFT.darkPrimary,
    darkPrimaryHover: overrides.darkMode?.colors?.primaryHover ?? DEFAULT_DRAFT.darkPrimaryHover,
  };
}

function draftToOverrides(draft: DraftState): TenantThemeOverrides {
  return {
    colors: {
      primary: draft.primary,
      primaryHover: draft.primaryHover,
      success: draft.success,
      warning: draft.warning,
      error: draft.error,
    },
    typography: {
      fontFamily: draft.fontFamily,
    },
    background: {
      page: {
        type: draft.bgType,
        value: draft.bgValue,
        ...(draft.bgType === 'image-overlay' && draft.bgOverlay ? { overlay: draft.bgOverlay } : {}),
      },
    },
    icons: {
      pack: draft.iconPack,
      strokeWidth: draft.strokeWidth,
    },
    radius: {
      scale: draft.radiusScale,
    },
    borders: {
      width: draft.borderWidth,
    },
    darkMode: {
      enabled: draft.darkEnabled,
      ...(draft.darkPrimary || draft.darkPrimaryHover
        ? {
            colors: {
              ...(draft.darkPrimary ? { primary: draft.darkPrimary } : {}),
              ...(draft.darkPrimaryHover ? { primaryHover: draft.darkPrimaryHover } : {}),
            },
          }
        : {}),
    },
  };
}

// ── Color Input Component ────────────────────────────────────────────────────

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

const ColorField: React.FC<ColorFieldProps> = ({ label, value, onChange }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-medium" style={{ color: '#1e293b' }}>
      {label}
    </label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-9 h-9 rounded-lg border cursor-pointer p-0.5"
        style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff' }}
      />
      <Input
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          if (/^#[0-9a-fA-F]{0,6}$/.test(v) || v === '') {
            onChange(v);
          }
        }}
        className="font-mono"
        style={{ maxWidth: 120 }}
      />
    </div>
  </div>
);

// ── Main Component ───────────────────────────────────────────────────────────

interface CompanyCustomizationTabProps {
  isOpen: boolean;
  onSaveStateChange?: (isSaving: boolean) => void;
}

export const CompanyCustomizationTab: React.FC<CompanyCustomizationTabProps> = ({ isOpen, onSaveStateChange }) => {
  const queryClient = useQueryClient();

  const { data: appData } = useQuery<AppResponse | undefined>({
    queryKey: ['app-data'],
    queryFn: async () => {
      try {
        const response = await api.get('apps/me');
        return response.data.data as AppResponse;
      } catch {
        return undefined;
      }
    },
    staleTime: 1000 * 60 * 10,
    enabled: isOpen,
  });

  const { data: currentOverrides, isLoading } = useQuery<TenantThemeOverrides | undefined>({
    queryKey: ['tenant-theme'],
    queryFn: async () => {
      try {
        const response = await api.get('companies/theme');
        return response.data.data as TenantThemeOverrides;
      } catch {
        return undefined;
      }
    },
    staleTime: 1000 * 60 * 5,
    enabled: isOpen,
  });

  const [draft, setDraft] = useState<DraftState>(DEFAULT_DRAFT);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (currentOverrides !== undefined) {
      setDraft(overridesToDraft(currentOverrides));
    }
  }, [currentOverrides]);

  const updateDraft = useCallback(<K extends keyof DraftState>(key: K, value: DraftState[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }, []);

  const saveMutation = useMutation({
    mutationFn: async (overrides: TenantThemeOverrides) => {
      const response = await api.patch('companies/theme', overrides);
      return response.data.data as TenantThemeOverrides;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-theme'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
    onSettled: () => {
      onSaveStateChange?.(false);
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      const response = await api.patch('companies/theme', {});
      return response.data.data as TenantThemeOverrides;
    },
    onSuccess: () => {
      setDraft({ ...DEFAULT_DRAFT });
      queryClient.invalidateQueries({ queryKey: ['tenant-theme'] });
    },
  });

  const handleSave = () => {
    onSaveStateChange?.(true);
    saveMutation.mutate(draftToOverrides(draft));
  };

  const handleReset = () => {
    resetMutation.mutate();
  };

  // ── Preview CSS variables ────────────────────────────────────────────────

  const previewVars = useMemo(() => {
    const overrides = draftToOverrides(draft);
    const appResolved = resolveTheme(appData?.themeOverrides, appData?.baseThemeId ?? 'apple');
    const resolved = resolveThemeFromConfig(overrides, appResolved);
    return getThemeCSSVars(resolved);
  }, [draft, appData]);

  // ── Render ─────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2"
          style={{ borderColor: '#8b5cf6' }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── 1. Cores (F85: FormSection shared) ────────────────────── */}
      <FormSection icon="circle" title="Cores" description="Defina as cores principais do sistema">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <ColorField label="Primária" value={draft.primary} onChange={(v) => updateDraft('primary', v)} />
          <ColorField label="Primária Hover" value={draft.primaryHover} onChange={(v) => updateDraft('primaryHover', v)} />
          <ColorField label="Sucesso" value={draft.success} onChange={(v) => updateDraft('success', v)} />
          <ColorField label="Aviso" value={draft.warning} onChange={(v) => updateDraft('warning', v)} />
          <ColorField label="Erro" value={draft.error} onChange={(v) => updateDraft('error', v)} />
        </div>
      </FormSection>

      {/* ── 2. Tipografia ─────────────────────────────────────────── */}
      <FormSection icon="type" title="Tipografia" description="Escolha a fonte do sistema">
        <div className="max-w-xs">
          <Select
            label="Família de fonte"
            options={FONT_OPTIONS}
            value={draft.fontFamily}
            onChange={(v) => updateDraft('fontFamily', v)}
          />
        </div>
        <p className="mt-3 text-sm" style={{ color: '#64748b', fontFamily: draft.fontFamily }}>
          Exemplo de texto com a fonte selecionada: &quot;A rápida raposa marrom.&quot;
        </p>
      </FormSection>

      {/* ── 3. Fundo da Página ────────────────────────────────────── */}
      <FormSection icon="image" title="Fundo da Página" description="Configure o fundo da área principal">
        <RadioGroup
          label="Tipo de fundo"
          options={BG_TYPE_OPTIONS}
          value={draft.bgType}
          onChange={(v) => updateDraft('bgType', v as PageBackgroundType)}
          orientation="horizontal"
        />
        <div className="max-w-md">
          <Input
            value={draft.bgValue}
            onChange={(e) => updateDraft('bgValue', e.target.value)}
            placeholder={draft.bgType === 'solid' ? '#f9fafb' : draft.bgType === 'gradient' ? 'linear-gradient(...)' : 'https://...'}
          />
          <p className="text-xs mt-1" style={{ color: '#64748b' }}>
            {draft.bgType === 'solid' && 'Informe um código de cor hexadecimal'}
            {draft.bgType === 'gradient' && 'Informe um CSS gradient'}
            {draft.bgType === 'image' && 'Informe a URL da imagem'}
            {draft.bgType === 'image-overlay' && 'Informe a URL da imagem'}
          </p>
        </div>
        {draft.bgType === 'image-overlay' && (
          <div className="max-w-md">
            <Input
              value={draft.bgOverlay}
              onChange={(e) => updateDraft('bgOverlay', e.target.value)}
              placeholder="rgba(0, 0, 0, 0.5)"
            />
            <p className="text-xs mt-1" style={{ color: '#64748b' }}>
              Cor de overlay sobre a imagem (ex: rgba)
            </p>
          </div>
        )}
      </FormSection>

      {/* ── 4. Ícones ─────────────────────────────────────────────── */}
      <FormSection icon="star" title="Ícones" description="Pacote de ícones e espessura do traço">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
          <Select
            label="Pacote de ícones"
            options={ICON_PACK_OPTIONS}
            value={draft.iconPack}
            onChange={(v) => updateDraft('iconPack', v as IconPackId)}
          />
          <Select
            label="Espessura do traço"
            options={STROKE_OPTIONS}
            value={String(draft.strokeWidth)}
            onChange={(v) => updateDraft('strokeWidth', parseFloat(v))}
          />
        </div>
      </FormSection>

      {/* ── 5. Bordas ─────────────────────────────────────────────── */}
      <FormSection icon="square" title="Bordas" description="Arredondamento e espessura das bordas">
        <RadioGroup
          label="Escala de arredondamento"
          options={RADIUS_OPTIONS}
          value={draft.radiusScale}
          onChange={(v) => updateDraft('radiusScale', v as RadiusScale)}
          variant="cards"
          orientation="horizontal"
        />
        <div className="max-w-xs">
          <Select
            label="Espessura da borda"
            options={BORDER_WIDTH_OPTIONS}
            value={draft.borderWidth}
            onChange={(v) => updateDraft('borderWidth', v as '0.5px' | '1px')}
          />
        </div>
      </FormSection>

      {/* ── 6. Dark Mode ──────────────────────────────────────────── */}
      <FormSection icon="moon" title="Dark Mode" description="Ative e personalize o modo escuro">
        <Switch
          label="Habilitar Dark Mode"
          description="Permite que os usuários ativem o modo escuro"
          checked={draft.darkEnabled}
          onChange={(v) => updateDraft('darkEnabled', v)}
        />
        {draft.darkEnabled && (
          <div className="grid grid-cols-2 gap-4 max-w-md pt-2">
            <ColorField
              label="Primária (Dark)"
              value={draft.darkPrimary || draft.primary}
              onChange={(v) => updateDraft('darkPrimary', v)}
            />
            <ColorField
              label="Primária Hover (Dark)"
              value={draft.darkPrimaryHover || draft.primaryHover}
              onChange={(v) => updateDraft('darkPrimaryHover', v)}
            />
          </div>
        )}
      </FormSection>

      {/* ── 7. Preview ────────────────────────────────────────────── */}
      <FormSection icon="eye" title="Preview" description="Visualize as alterações em tempo real">
        <div
          className="rounded-xl border overflow-hidden"
          style={{ borderColor: '#e5e7eb' }}
        >
          {/* Mini mockup */}
          <div className="flex h-48">
            {/* Sidebar preview */}
            <div
              className="w-14 flex flex-col items-center py-3 gap-2 shrink-0"
              style={{
                backgroundColor: previewVars['--theme-sidebar-background'] ?? '#1e1b3a',
                borderRight: `1px solid ${previewVars['--theme-sidebar-border'] ?? 'rgba(255,255,255,0.1)'}`,
              }}
            >
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: previewVars['--theme-colors-primary'] ?? draft.primary }}
              >
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#fff' }} />
              </div>
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-5 h-5 rounded"
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                />
              ))}
            </div>

            {/* Content area */}
            <div className="flex-1 flex flex-col" style={{ backgroundColor: previewVars['--theme-page-background'] ?? '#f9fafb' }}>
              {/* Header */}
              <div
                className="h-8 flex items-center px-3 shrink-0"
                style={{
                  backgroundColor: previewVars['--theme-header-background'] ?? '#fff',
                  borderBottom: `1px solid ${previewVars['--theme-header-border'] ?? '#e5e7eb'}`,
                }}
              >
                <div className="w-16 h-2 rounded" style={{ backgroundColor: previewVars['--theme-colors-primary'] ?? draft.primary, opacity: 0.6 }} />
              </div>

              {/* Cards */}
              <div className="flex-1 p-3 grid grid-cols-3 gap-2 content-start">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="rounded-lg p-2"
                    style={{
                      backgroundColor: previewVars['--theme-card-background'] ?? '#fff',
                      border: `${draft.borderWidth} solid ${previewVars['--theme-card-border'] ?? '#e5e7eb'}`,
                      borderRadius: previewVars['--radius-md'] ?? '8px',
                    }}
                  >
                    <div className="w-full h-2 rounded mb-1.5" style={{ backgroundColor: previewVars['--theme-colors-primary'] ?? draft.primary, opacity: 0.3 }} />
                    <div className="w-3/4 h-1.5 rounded" style={{ backgroundColor: previewVars['--theme-card-muted'] ?? '#94a3b8', opacity: 0.4 }} />
                  </div>
                ))}

                {/* Button preview */}
                <div className="col-span-3 flex items-center gap-2 mt-1">
                  <div
                    className="h-6 px-3 rounded-md flex items-center"
                    style={{
                      backgroundColor: previewVars['--theme-button-primary'] ?? draft.primary,
                      borderRadius: previewVars['--radius-md'] ?? '8px',
                    }}
                  >
                    <span className="text-[10px] font-medium" style={{ color: previewVars['--theme-button-primary-text'] ?? '#fff' }}>
                      Botão
                    </span>
                  </div>
                  <div
                    className="h-6 px-3 rounded-md flex items-center"
                    style={{
                      backgroundColor: previewVars['--theme-colors-success'] ?? draft.success,
                      borderRadius: previewVars['--radius-md'] ?? '8px',
                    }}
                  >
                    <span className="text-[10px] font-medium text-white">Sucesso</span>
                  </div>
                  <div
                    className="h-6 px-3 rounded-md flex items-center"
                    style={{
                      backgroundColor: previewVars['--theme-colors-error'] ?? draft.error,
                      borderRadius: previewVars['--radius-md'] ?? '8px',
                    }}
                  >
                    <span className="text-[10px] font-medium text-white">Erro</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Font preview bar */}
          <div
            className="px-3 py-2 text-xs flex items-center justify-between"
            style={{
              borderTop: '1px solid #e5e7eb',
              backgroundColor: previewVars['--theme-card-background'] ?? '#fff',
              color: previewVars['--theme-card-muted'] ?? '#94a3b8',
              fontFamily: draft.fontFamily,
            }}
          >
            <span>Fonte: {draft.fontFamily}</span>
            <span>Raio: {draft.radiusScale}</span>
            <span>Borda: {draft.borderWidth}</span>
          </div>
        </div>
      </FormSection>

      {/* ── 8. Actions ────────────────────────────────────────────── */}
      <div className="flex items-center justify-between pt-2">
        <Button
          variant="secondary"
          onClick={handleReset}
          disabled={resetMutation.isPending}
          data-testid="company-customization-reset"
        >
          <Icon name="rotate-ccw" className="w-4 h-4 mr-2" />
          Restaurar Padrão
        </Button>

        <div className="flex items-center gap-3">
          {saved && (
            <span className="text-sm flex items-center gap-1" style={{ color: '#22c55e' }} data-testid="company-customization-saved">
              <Icon name="check" className="w-4 h-4" />
              Salvo
            </span>
          )}
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saveMutation.isPending}
            data-testid="company-customization-save"
          >
            {saveMutation.isPending ? (
              <>
                <Icon name="loader" className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Icon name="save" className="w-4 h-4 mr-2" />
                Salvar Personalização
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

CompanyCustomizationTab.displayName = 'CompanyCustomizationTab';
